import React, { Component } from "react";
import Loading from "../../common/components/loading/loading";
import {
  getFilteredProductForMarket,
  refilterProductForMarketDateChange,
} from "../../common/services/products/productUtilities";
import { getMarketPriceDriverCombinations } from "../../common/services/markets/marketPriceDriverCombinationService";
import { getMarketPricesByMarket } from "../../common/services/marketdata/marketPriceUtilities";
import {
  getAggregateMarksByMarket,
  getAggregateForwardMarksByMarket,
  getAggregateForwardMarksAsOfByMarket,
  getInterpolatedAggregateMarksByMarket,
} from "../../common/services/marketdata/markService";
import MarksForwardTable from "./marksForwardTable";
import MarksHistoryTable from "./marksHistoryTable";
import MarketDataPriceIndicator from "./marketDataPriceIndicator";
import AttributeSpreads from "./attributeSpreads";
import TradesTable from "./tradesTable";
import { withTranslation } from "react-i18next";
import ListGroup from "react-bootstrap/ListGroup";
import Input from "../../common/components/form/input";
import { getAggregateTradesByMarket } from "../../common/services/marketdata/tradeService";
import Modal from "react-bootstrap/Modal";
import { formatDate, priorBusinessDay } from "../../common/services/utilities";
import { memoize } from "lodash";
import { HistoricalReportingPeriodicity } from "../../common/enumerations/enums";

class MarketDataDashboard extends Component {
  _lastDataLoadRequestId = 0;

  state = {
    marketAttributes: [],
    marks: [],
    forwardMarks: [],
    trades: [],
    marketPrices: [],
    forwardReportingPeriodicity: "",
    historicalReportingPeriodicity: "",
    isLoading: true,
    historicalDataIsLoading: true,
    forwardDataIsLoading: true,
    tradeDataIsLoading: true,
    marketPriceIsLoading: true,
    marketDate: null,
    fromDate: null,
    toDate: null,
    termScheduleId: null,
    originalQualityAttributeValues: null,
    filteredProduct: null,
    marketPriceDriverCombinations: [],
    priceBasisId: null,
    currencyId: null,
    uomId: null,
    pricePrecision: 2,
    volumePrecision: 0,
    currencySymbol: "$",
    showAttributeSpreadModal: false,
  };

  async componentDidMount() {
    const { market, venue, termSchedules } = this.props;
    const { productId, marketId } = market;

    const termSchedule =
      termSchedules &&
      termSchedules.find((x) => x.termScheduleId === market.termScheduleId);

    const marketDate = new Date().toLocaleDateString("fr-CA");

    const { forwardReportingPeriodicity, historicalReportingPeriodicity } =
      termSchedule;

    const [
      { originalQualityAttributeValues, filteredProduct },
      marketPriceDriverCombinations,
    ] = await Promise.all([
      getFilteredProductForMarket(productId, marketId, marketDate),
      getMarketPriceDriverCombinations(marketId).catch((error) => {
        console.error("Error loading market price driver combinations:", error);
        return [];
      }),
    ]);

    const timeZone = venue && this.getTimeZone(venue.timeZone);

    this.setState({
      marketDate,
      originalQualityAttributeValues: originalQualityAttributeValues,
      filteredProduct: filteredProduct,
      marketPriceDriverCombinations: marketPriceDriverCombinations || [],
      marketAttributes: market.marketAttributes,
      termScheduleId: market.termScheduleId,
      forwardReportingPeriodicity: forwardReportingPeriodicity,
      historicalReportingPeriodicity: historicalReportingPeriodicity,
      priceBasisId: market.marketAttributes[0].priceBasisId,
      currencyId: market.marketAttributes[0].currencyId,
      uomId: market.marketAttributes[0].uomId,
      pricePrecision: market.marketAttributes[0].pricePrecision ?? 2,
      volumePrecision: market.marketAttributes[0].volumePrecision ?? 0,
      currencySymbol: market.marketAttributes[0].currencySymbol ?? "$",
      timeZone,
      isLoading: false,
    });

    if (marketDate && market && termSchedule) {
      await this.loadData(
        marketDate,
        termSchedule.historicalReportingPeriodicity,
        termSchedule.forwardReportingPeriodicity,
      );
    }
  }

  componentWillUnmount() {
    this._lastDataLoadRequestId += 1;
  }

  getTimeZone = memoize((timeZone) => {
    return `${timeZone} (${
      Intl.DateTimeFormat("us", {
        timeZoneName: "short",
        timeZone: timeZone,
      })
        .formatToParts()
        .find((i) => i.type === "timeZoneName").value
    })`;
  });

  loadData = async (
    marketDate,
    historicalReportingPeriodicity,
    forwardReportingPeriodicity,
  ) => {
    const requestId = this._lastDataLoadRequestId + 1;
    this._lastDataLoadRequestId = requestId;

    const lookbackWindow = historicalReportingPeriodicity === "Hour" ? 1 : 7;
    const tradeLookbackWindow = 14;
    const fromDate = this.calculateFromDate(marketDate, lookbackWindow);
    const curveFromDate = this.calculateCurveFromDate(
      marketDate,
      forwardReportingPeriodicity,
    );
    const tradeFromDate = this.calculateFromDate(
      marketDate,
      tradeLookbackWindow,
    );
    const toDate = this.calculateToDate(marketDate);

    this.setState({
      fromDate,
      toDate,
      historicalDataIsLoading: true,
      forwardDataIsLoading: true,
      tradeDataIsLoading: true,
      marketPriceIsLoading: true,
    });

    const { market } = this.props;
    const { marketId } = market;

    const [tradesResult, marksResult, marketPricesResult] =
      await Promise.allSettled([
        this.fetchTrades(marketId, tradeFromDate, toDate),
        this.fetchMarks(
          marketId,
          fromDate,
          toDate,
          curveFromDate,
          historicalReportingPeriodicity,
          forwardReportingPeriodicity,
        ),
        this.fetchMarketPrices(
          marketId,
          toDate,
          historicalReportingPeriodicity,
        ),
      ]);

    if (this._lastDataLoadRequestId !== requestId) {
      return;
    }

    const nextState = {
      historicalDataIsLoading: false,
      forwardDataIsLoading: false,
      tradeDataIsLoading: false,
      marketPriceIsLoading: false,
    };

    if (tradesResult.status === "fulfilled") {
      nextState.trades = tradesResult.value;
    } else {
      console.error("Error loading aggregate trades:", tradesResult.reason);
      nextState.trades = [];
    }

    if (marksResult.status === "fulfilled") {
      nextState.marks = marksResult.value.marks;
      nextState.forwardMarks = marksResult.value.forwardMarks;
    } else {
      console.error("Error loading aggregate marks:", marksResult.reason);
      nextState.marks = [];
      nextState.forwardMarks = [];
    }

    if (marketPricesResult.status === "fulfilled") {
      nextState.marketPrices = marketPricesResult.value;
    } else {
      console.error("Error loading market prices:", marketPricesResult.reason);
      nextState.marketPrices = [];
    }

    this.setState(nextState);
  };

  calculateFromDate = (marketDate, lookbackWindow) => {
    let fromDate = new Date(marketDate);
    fromDate.setMinutes(fromDate.getMinutes() + fromDate.getTimezoneOffset());
    fromDate.setDate(fromDate.getDate() - lookbackWindow);
    return fromDate;
  };

  calculateCurveFromDate = (marketDate, forwardReportingPeriodicity) => {
    const curveFromDate = new Date(marketDate);
    curveFromDate.setMinutes(
      curveFromDate.getMinutes() + curveFromDate.getTimezoneOffset(),
    );

    if (forwardReportingPeriodicity === "Year") {
      curveFromDate.setDate(curveFromDate.getDate() - 365);
    } else {
      curveFromDate.setDate(curveFromDate.getDate() - 1);
    }

    return priorBusinessDay(curveFromDate);
  };

  calculateToDate = (marketDate) => {
    const toDate = new Date(marketDate);
    toDate.setMinutes(toDate.getMinutes() + toDate.getTimezoneOffset());
    return toDate;
  };

  fetchTrades = async (marketId, fromDate, toDate) => {
    return await getAggregateTradesByMarket(
      marketId,
      formatDate(fromDate),
      formatDate(toDate),
    );
  };

  fetchMarks = async (
    marketId,
    fromDate,
    toDate,
    curveFromDate,
    historicalReportingPeriodicity,
    forwardReportingPeriodicity,
  ) => {
    const marksPromise =
      historicalReportingPeriodicity === HistoricalReportingPeriodicity.Year
        ? getInterpolatedAggregateMarksByMarket(
            marketId,
            formatDate(fromDate),
            formatDate(toDate),
          )
        : getAggregateMarksByMarket(
            marketId,
            formatDate(fromDate),
            formatDate(toDate),
          );
    const forwardMarksPromise =
      forwardReportingPeriodicity === HistoricalReportingPeriodicity.Year
        ? getAggregateForwardMarksAsOfByMarket(marketId, formatDate(toDate))
        : getAggregateForwardMarksByMarket(
            marketId,
            formatDate(curveFromDate),
            formatDate(toDate),
          );

    const [marks, forwardMarks] = await Promise.all([
      marksPromise,
      forwardMarksPromise,
    ]);

    return {
      marks: marks.filter((x) => x.markState === "Approved"),
      forwardMarks: forwardMarks.filter((x) => x.markState === "Approved"),
    };
  };

  fetchMarketPrices = async (
    marketId,
    marketDate,
    historicalReportingPeriodicity,
  ) => {
    return await getMarketPricesByMarket(
      marketId,
      marketDate.toLocaleDateString("fr-CA"),
      historicalReportingPeriodicity === HistoricalReportingPeriodicity.Year,
    );
  };

  handleMarketDateChange = async (marketDate) => {
    // Update date immediately so input doesn't reset
    this.setState({ marketDate });

    const { historicalReportingPeriodicity, forwardReportingPeriodicity } =
      this.state;
    const { market } = this.props;
    const { marketId } = market;

    // Perform refiltering asynchronously in background
    refilterProductForMarketDateChange(this, marketDate, marketId);

    // Load data asynchronously (existing behavior)
    this.loadData(
      marketDate,
      historicalReportingPeriodicity,
      forwardReportingPeriodicity,
    );
  };

  handlemarketAttributeSelect = (marketAttribute) => {
    this.setState({
      priceBasisId: marketAttribute.priceBasisId,
      currencyId: marketAttribute.currencyId,
      uomId: marketAttribute.uomId,
    });
  };

  handleAttributeSpreadClose = () => {
    this.setState({ showAttributeSpreadModal: false });
  };

  getFilteredData = (data, priceBasisId, uomId, currencyId) => {
    return data.filter(
      (x) =>
        x.priceBasisId === priceBasisId &&
        x.uomId === uomId &&
        x.currencyId === currencyId,
    );
  };

  render() {
    const {
      timeZone,
      marketAttributes,
      termScheduleId,
      historicalReportingPeriodicity,
      forwardReportingPeriodicity,
      isLoading,
      historicalDataIsLoading,
      forwardDataIsLoading,
      tradeDataIsLoading,
      marketPriceIsLoading,
      toDate,
      fromDate,
      marketDate,
      priceBasisId,
      currencyId,
      uomId,
      pricePrecision,
      volumePrecision,
      currencySymbol,
      showAttributeSpreadModal,
      filteredProduct,
      marketPriceDriverCombinations,
    } = this.state;

    const product = filteredProduct;

    const marks = this.getFilteredData(
      this.state.marks,
      priceBasisId,
      uomId,
      currencyId,
    );
    const forwardMarks = this.getFilteredData(
      this.state.forwardMarks,
      priceBasisId,
      uomId,
      currencyId,
    );
    const trades = this.getFilteredData(
      this.state.trades,
      priceBasisId,
      uomId,
      currencyId,
    );
    const marketPrices = this.getFilteredData(
      this.state.marketPrices,
      priceBasisId,
      uomId,
      currencyId,
    );

    const currentMarketPrice = marketPrices.find(
      (x) => x.qualityAttributeValues === null && x.termName === "Latest",
    );
    const mtdMarketPrice = marketPrices.find(
      (x) => x.qualityAttributeValues === null && x.termName === "MTD",
    );

    if (isLoading) return <Loading />;

    const { t, market } = this.props;
    const { marketId } = market;
    const marketName = (market?.name ?? market?.marketName ?? "").trim();
    const renderSectionTitle = (label) => (
      <span className="section-title-label">{label}</span>
    );

    const endDate = new Date().toLocaleDateString("fr-CA");

    return (
      <div className="container-fluid daily-summary-dashboard">
        <Modal
          size="lg"
          show={showAttributeSpreadModal}
          onHide={this.handleAttributeSpreadClose}
          //centered
          backdrop="static"
          keyboard={false}
        >
          <Modal.Header closeButton>
            <Modal.Title>{t("QualityAttributeDifferentials")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <AttributeSpreads
              marks={marks}
              pricePrecision={pricePrecision}
              currencySymbol={currencySymbol}
              product={product}
              termScheduleId={termScheduleId}
              fromDate={fromDate}
              toDate={toDate}
            />
          </Modal.Body>
        </Modal>
        {marketName && (
          <h1 className="daily-summary-market-title market-name-page-title">
            {marketName}
          </h1>
        )}
        <div className="row">
          <div className="col-md-3"></div>
          <div className="col-md-6">
            <div className="selector-title-class">{t("PriceBasis")}</div>
            <ListGroup horizontal="sm" className="price-basis-selector">
              {marketAttributes.map((attribute, index) => (
                <ListGroup.Item
                  className="market-attribute-selector"
                  key={index}
                  action
                  active={priceBasisId === attribute.priceBasisId}
                  onClick={() => this.handlemarketAttributeSelect(attribute)}
                >
                  {attribute.priceBasisName +
                    " (" +
                    attribute.currencyName +
                    "/" +
                    attribute.uomName +
                    ")"}
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
          <div className="col-md-3"></div>
        </div>
        <div className="row align-items-end">
          <div className="col-auto">
            <Input
              type="date"
              max={endDate}
              onChange={(e) =>
                this.handleMarketDateChange(e.currentTarget.value)
              }
              value={marketDate}
              label={
                <span className="selector-title-class">{t("MarketDate")}</span>
              }
            />
          </div>
          <div className="col-auto">
            {product.qualityAttributes.filter((x) => x.isPriceDriver).length >
              0 && (
              <div className="form-group daily-summary-attribute-differentials-group">
                <label
                  htmlFor="attribute-differentials-button"
                  className="daily-summary-control-spacer-label"
                >
                  &nbsp;
                </label>
                <button
                  onClick={() =>
                    this.setState({ showAttributeSpreadModal: true })
                  }
                  className="btn btn-sm btn-primary daily-summary-attribute-differentials-button"
                  id="attribute-differentials-button"
                >
                  {t("QualityAttributeDifferentials")}
                </button>
              </div>
            )}
          </div>
          <div className="col"></div>
          <div className="col-auto align-self-start time-zone-block">
            <div className="selector-title-class time-zone-title">
              {t("TimeZone")}
            </div>
            <div>{timeZone}</div>
          </div>
        </div>

        <div className="d-flex flex-wrap gap-1">
          <div className="flex-fill tile section-tile shadow-card">
            <MarketDataPriceIndicator
              marketPrice={currentMarketPrice}
              marketPriceIsLoading={marketPriceIsLoading}
              currencySymbol={currencySymbol}
              pricePrecision={pricePrecision}
              title={renderSectionTitle("Latest Price")}
              variant="latest"
            />
          </div>
          <div className="flex-fill tile section-tile shadow-card">
            <MarketDataPriceIndicator
              marketPrice={mtdMarketPrice}
              marketPriceIsLoading={marketPriceIsLoading}
              currencySymbol={currencySymbol}
              pricePrecision={pricePrecision}
              title={renderSectionTitle("MTD Price")}
              variant="mtd"
            />
          </div>
        </div>

        <div className="d-flex flex-wrap gap-1">
          <div className="flex-fill tile section-tile shadow-card">
            <h5 className="section-title">
              {renderSectionTitle(t("SpotHistory"))}
            </h5>
            {historicalDataIsLoading ? (
              <Loading />
            ) : (
              <MarksHistoryTable
                marks={marks}
                product={product}
                marketPriceDriverCombinations={marketPriceDriverCombinations}
                marketId={marketId}
                marketDate={marketDate}
                termScheduleId={termScheduleId}
                historicalReportingPeriodicity={
                  historicalReportingPeriodicity
                }
                fromDate={fromDate}
                toDate={toDate}
                priceBasisId={priceBasisId}
                currencyId={currencyId}
                uomId={uomId}
                pricePrecision={pricePrecision}
                volumePrecision={volumePrecision}
                currencySymbol={currencySymbol}
              />
            )}
          </div>
          <div className="flex-fill tile section-tile shadow-card">
            <h5 className="section-title">
              {renderSectionTitle(t("ForwardCurve"))}
            </h5>
            {forwardDataIsLoading ? (
              <Loading />
            ) : (
              <MarksForwardTable
                marks={forwardMarks}
                product={product}
                marketPriceDriverCombinations={marketPriceDriverCombinations}
                marketId={marketId}
                termScheduleId={termScheduleId}
                forwardReportingPeriodicity={forwardReportingPeriodicity}
                marketDate={marketDate}
                priceBasisId={priceBasisId}
                currencyId={currencyId}
                uomId={uomId}
                pricePrecision={pricePrecision}
                volumePrecision={volumePrecision}
                currencySymbol={currencySymbol}
              />
            )}
          </div>
        </div>
        {(tradeDataIsLoading || trades.length > 0) && (
          <div
            className="d-flex flex-wrap gap-1"
            style={{ paddingBottom: "30px" }}
          >
            <div className="flex-fill tile">
              <h5 className="section-title">
                {renderSectionTitle(t("RecentTrades"))}
              </h5>
              {tradeDataIsLoading ? (
                <Loading />
              ) : (
                <TradesTable
                  trades={trades}
                  product={product}
                  marketId={marketId}
                  termScheduleId={termScheduleId}
                  fromDate={fromDate}
                  tradeDate={marketDate}
                  priceBasisId={priceBasisId}
                  currencyId={currencyId}
                  uomId={uomId}
                  pricePrecision={pricePrecision}
                  volumePrecision={volumePrecision}
                  currencySymbol={currencySymbol}
                />
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default withTranslation(["marketdata"])(MarketDataDashboard);
